import type {
  DataGridColumnOrderState,
  DataGridColumnPinningState,
  DataGridColumnSizingState,
  DataGridColumnVisibilityState,
  DataGridFilterState,
  DataGridSortingState,
} from '@ibsys/kit';
import { pruneStateForHiddenColumns, useDataGridColumnsState } from '@ibsys/kit';
import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  type DataGridColumnActions,
  type DataGridColumnSettings,
  type DataGridColumnStateProps,
  isSameColumnOrder,
  isSameColumnPinning,
  isSameColumnVisibility,
  restoreColumnOrder,
  restoreColumnVisibility,
} from './columnState';
import { fromDataGridFilters, fromDataGridSorting } from './dataGridAdapters';

/** Пустые ширины колонок: стабильная ссылка, пока пользователь их не задавал */
const EMPTY_COLUMN_SIZING: DataGridColumnSizingState = {};

/** Пустой порядок колонок: состав списка известен только из данных */
const EMPTY_COLUMN_ORDER: DataGridColumnOrderState = [];

type Params = {
  settings: DataGridColumnSettings;
  actions: DataGridColumnActions;
  /** Отбор грида: по скрытой колонке он в запрос не идёт */
  filters: DataGridFilterState;
  /** Порядок грида: по скрытой колонке он в запрос не идёт */
  sorting: DataGridSortingState;
  /** Видимость по умолчанию — явное значение по каждой колонке, известной списку */
  defaultVisibility: DataGridColumnVisibilityState;
  /** Порядок по умолчанию; пустой — состав колонок известен только из данных */
  defaultOrder?: DataGridColumnOrderState;
  /** Пока true — сохранённое состояние к гриду не применяется: состав колонок ещё грузится */
  isPending?: boolean;
  /** Ширина колонки живёт только до конца сеанса и в настройки не пишется */
  isEphemeralColumn?: (columnId: string) => boolean;
};

type Result = {
  /** Пропы состояния колонок для DataGrid */
  gridColumnProps: DataGridColumnStateProps;
  /** Видимость с явным значением по каждой известной колонке */
  columnVisibility: DataGridColumnVisibilityState;
  /** Сохранённое состояние применено к гриду */
  isReady: boolean;
};

/**
 * Состав, порядок, закрепление и ширина колонок списка: состояние для грида и его запись
 * в настройки пользователя. Умолчания и специфику списка передаёт страница, само же согласование
 * «состояние грида ↔ настройки» одно на все списки.
 *
 * Настройки приходят после авторизации, то есть уже после первого рендера, поэтому сохранённое
 * состояние применяется к гриду отдельным эффектом. Сеттеры кита обратной записи не вызывают —
 * настройки от применения сохранённого не «грязнятся».
 */
export const useDataGridColumnsSettings = ({
  settings,
  actions,
  filters,
  sorting,
  defaultVisibility,
  defaultOrder = EMPTY_COLUMN_ORDER,
  isPending = false,
  isEphemeralColumn,
}: Params): Result => {
  const { gridProps } = useDataGridColumnsState({
    defaultOrder,
    defaultVisibility,
    defaultPinning: settings.columnPinning,
  });

  const columnVisibility = useMemo(
    () =>
      settings.columnVisibility
        ? restoreColumnVisibility(settings.columnVisibility, defaultVisibility)
        : defaultVisibility,
    [settings.columnVisibility, defaultVisibility],
  );

  const columnOrder = useMemo(
    () => (settings.columnOrder ? restoreColumnOrder(settings.columnOrder, defaultOrder) : undefined),
    [settings.columnOrder, defaultOrder],
  );

  const [isReady, setIsReady] = useState(false);

  // Согласование идемпотентно: состояние, совпадающее с текущим по содержимому, гриду не задаётся
  // заново. Иначе состав колонок, пересобранный на очередном рендере (он известен только из данных
  // и при их отсутствии получается заново), задавал бы гриду новое состояние на каждом проходе —
  // и рендер зацикливался бы.
  useEffect(() => {
    if (isPending) return;
    if (!isSameColumnVisibility(gridProps.columnVisibility, columnVisibility)) {
      gridProps.onColumnVisibilityChange(columnVisibility);
    }
    if (columnOrder && !isSameColumnOrder(gridProps.columnOrder, columnOrder)) {
      gridProps.onColumnOrderChange(columnOrder);
    }
    if (settings.columnPinning && !isSameColumnPinning(gridProps.columnPinning, settings.columnPinning)) {
      gridProps.onColumnPinningChange(settings.columnPinning);
    }
    setIsReady(true);
  }, [
    isPending,
    columnVisibility,
    columnOrder,
    settings.columnPinning,
    gridProps.columnVisibility,
    gridProps.columnOrder,
    gridProps.columnPinning,
    gridProps.onColumnVisibilityChange,
    gridProps.onColumnOrderChange,
    gridProps.onColumnPinningChange,
  ]);

  const handleColumnOrderChange = useCallback(
    (next: DataGridColumnOrderState) => {
      gridProps.onColumnOrderChange(next);
      actions.setColumnState({ columnOrder: next });
    },
    [actions, gridProps.onColumnOrderChange],
  );

  const handleColumnVisibilityChange = useCallback(
    (next: DataGridColumnVisibilityState) => {
      gridProps.onColumnVisibilityChange(next);
      // Кит нормализует состояние, убирая явные true, а колонка без записи считается видимой.
      // В настройки пишем явное значение по каждой колонке — иначе скрытая по умолчанию
      // колонка, включённая пользователем, возвращалась бы скрытой.
      const explicit: DataGridColumnVisibilityState = {};
      for (const columnId of Object.keys(defaultVisibility)) explicit[columnId] = next[columnId] !== false;
      actions.setColumnState({ columnVisibility: explicit });

      // Отбор и порядок по скрытой колонке в запрос не идут
      const pruned = pruneStateForHiddenColumns({ filters, sorting, columnVisibility: next });
      actions.onFiltersChange(fromDataGridFilters(pruned.filters));
      actions.onSortingChange(fromDataGridSorting(pruned.sorting));
    },
    [actions, filters, sorting, defaultVisibility, gridProps.onColumnVisibilityChange],
  );

  const handleColumnPinningChange = useCallback(
    (next: DataGridColumnPinningState) => {
      gridProps.onColumnPinningChange(next);
      actions.setColumnState({ columnPinning: next });
    },
    [actions, gridProps.onColumnPinningChange],
  );

  // Ширина эфемерных колонок живёт только до конца сеанса: сами колонки приходят и уходят вместе
  // с режимом, который их порождает, поэтому в настройки (и в пресет) их ширина не попадает —
  // как не попадают их видимость и положение.
  const [ephemeralSizing, setEphemeralSizing] = useState<DataGridColumnSizingState>({});

  const columnSizing = useMemo<DataGridColumnSizingState>(() => {
    const saved = settings.columnSizing ?? EMPTY_COLUMN_SIZING;
    return Object.keys(ephemeralSizing).length > 0 ? { ...saved, ...ephemeralSizing } : saved;
  }, [settings.columnSizing, ephemeralSizing]);

  // Грид зовёт обработчик по окончании перетаскивания края (пока пользователь тянет, ширина живёт
  // в его собственном preview-состоянии), поэтому запись в настройки идёт без задержки
  const handleColumnSizingChange = useCallback(
    (next: DataGridColumnSizingState) => {
      const persisted: DataGridColumnSizingState = {};
      const ephemeral: DataGridColumnSizingState = {};
      for (const [columnId, size] of Object.entries(next)) {
        if (isEphemeralColumn?.(columnId)) ephemeral[columnId] = size;
        else persisted[columnId] = size;
      }
      // Пока эфемерных ширин нет и не было, состояние остаётся прежним объектом — иначе обычное
      // перетаскивание края колонки давало бы лишний ререндер вместо bail-out
      setEphemeralSizing((prev) =>
        Object.keys(ephemeral).length > 0 || Object.keys(prev).length > 0 ? ephemeral : prev,
      );
      // Пустой набор пишем как «ширин нет»: иначе снимок таблицы отличался бы от снимка пресета,
      // в котором ширин нет, и пресет выглядел бы изменённым
      actions.setColumnState({ columnSizing: Object.keys(persisted).length > 0 ? persisted : undefined });
    },
    [actions, isEphemeralColumn],
  );

  const gridColumnProps = useMemo<DataGridColumnStateProps>(
    () => ({
      ...gridProps,
      onColumnOrderChange: handleColumnOrderChange,
      onColumnVisibilityChange: handleColumnVisibilityChange,
      onColumnPinningChange: handleColumnPinningChange,
      columnSizing,
      onColumnSizingChange: handleColumnSizingChange,
    }),
    [
      gridProps,
      handleColumnOrderChange,
      handleColumnVisibilityChange,
      handleColumnPinningChange,
      columnSizing,
      handleColumnSizingChange,
    ],
  );

  return { gridColumnProps, columnVisibility, isReady };
};
